<div class="w-full bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm flex flex-col">
  <!-- Header with back button -->
  <div class="flex items-center gap-3 px-4 py-3 bg-violet-50 border-b border-gray-100">
    <button
      type="button"
      (click)="onBack()"
      class="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-violet-100 transition-colors cursor-pointer text-violet-600"
    >
      <lucide-icon [img]="icons.ArrowLeft" class="w-5 h-5"></lucide-icon>
    </button>
    <div class="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100 text-violet-600">
      <lucide-icon [img]="icons.User" class="w-5 h-5"></lucide-icon>
    </div>
    <div class="flex-1 min-w-0">
      <div class="text-sm font-semibold text-gray-900 truncate">{{ student().studentName }}</div>
      <div class="text-xs text-violet-600">
        Score: {{ formatScore() }}
        @if (student().score?.percentage !== undefined) {
          <span class="ml-1">({{ student().score!.percentage | number:'1.0-0' }}%)</span>
        }
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-y-auto">
    @if (isLoading()) {
      <!-- Loading State -->
      <div class="flex flex-col items-center justify-center py-16 text-gray-500">
        <lucide-icon [img]="icons.Loader2" class="w-8 h-8 animate-spin mb-3"></lucide-icon>
        <span class="text-sm">Loading student details...</span>
      </div>
    } @else if (error()) {
      <!-- Error State -->
      <div class="flex flex-col items-center justify-center py-16 text-red-500">
        <lucide-icon [img]="icons.AlertTriangle" class="w-8 h-8 mb-3"></lucide-icon>
        <span class="text-sm">{{ error() }}</span>
      </div>
    } @else if (resultDetail()) {
      <div class="divide-y divide-gray-100">
        <!-- Overall Feedback Section -->
        <div>
          <div class="px-4 py-3 bg-gray-50/50">
            <div class="flex items-center gap-2">
              <lucide-icon [img]="icons.MessageSquare" class="w-4 h-4 text-violet-500"></lucide-icon>
              <span class="font-semibold text-gray-800 text-sm">Overall Feedback</span>
            </div>
          </div>
          <div class="px-4 py-3">
            <p class="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{{ resultDetail()!.overallFeedback }}</p>
          </div>
        </div>

        <!-- Strengths Section -->
        @if (resultDetail()!.strengths.length) {
          <div>
            <div class="px-4 py-3 bg-green-50/50">
              <div class="flex items-center gap-2">
                <lucide-icon [img]="icons.Lightbulb" class="w-4 h-4 text-green-500"></lucide-icon>
                <span class="font-semibold text-gray-800 text-sm">Strengths</span>
                <span class="text-xs text-gray-500 bg-green-100 px-2 py-0.5 rounded-full">{{ resultDetail()!.strengths.length }}</span>
              </div>
            </div>
            <div class="px-4 py-3">
              <ul class="space-y-2">
                @for (strength of resultDetail()!.strengths; track strength) {
                  <li class="flex items-start gap-2 text-sm text-gray-700">
                    <lucide-icon [img]="icons.CheckCircle2" class="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5"></lucide-icon>
                    <span>{{ strength }}</span>
                  </li>
                }
              </ul>
            </div>
          </div>
        }

        <!-- Areas for Improvement Section -->
        @if (resultDetail()!.areasForImprovement.length) {
          <div>
            <div class="px-4 py-3 bg-amber-50/50">
              <div class="flex items-center gap-2">
                <lucide-icon [img]="icons.Target" class="w-4 h-4 text-amber-500"></lucide-icon>
                <span class="font-semibold text-gray-800 text-sm">Areas for Improvement</span>
                <span class="text-xs text-gray-500 bg-amber-100 px-2 py-0.5 rounded-full">{{ resultDetail()!.areasForImprovement.length }}</span>
              </div>
            </div>
            <div class="px-4 py-3">
              <ul class="space-y-2">
                @for (area of resultDetail()!.areasForImprovement; track area) {
                  <li class="flex items-start gap-2 text-sm text-gray-700">
                    <lucide-icon [img]="icons.AlertTriangle" class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"></lucide-icon>
                    <span>{{ area }}</span>
                  </li>
                }
              </ul>
            </div>
          </div>
        }

        <!-- Questions Breakdown Section -->
        @if (questionsData().length) {
          <div>
            <div class="px-4 py-3 bg-gray-50/50">
              <div class="flex items-center gap-2">
                <lucide-icon [img]="icons.FileText" class="w-4 h-4 text-violet-500"></lucide-icon>
                <span class="font-semibold text-gray-800 text-sm">Questions Breakdown</span>
                <span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{{ questionsData().length }} questions</span>
              </div>
            </div>
            <div class="divide-y divide-gray-100">
              @for (question of questionsData(); track question.itemId) {
                <div>
                  <!-- Question Header (Accordion Toggle) -->
                  <button
                    type="button"
                    (click)="toggleQuestion(question.itemId)"
                    class="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                  >
                    <span class="flex-1 text-sm text-gray-700 font-medium line-clamp-2">
                      <span class="text-gray-500">{{ question.questionNumber }}.</span> {{ question.questionText }}
                    </span>
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <span
                        class="text-sm font-semibold"
                        [ngClass]="getScoreColorClass(question.score.percentage)"
                      >
                        {{ question.score.earnedPoints }}/{{ question.score.maxPoints }}
                      </span>
                      <lucide-icon
                        [img]="icons.ChevronDown"
                        class="w-4 h-4 text-gray-400 transition-transform duration-200"
                        [class.rotate-180]="isQuestionExpanded(question.itemId)"
                      ></lucide-icon>
                    </div>
                  </button>

                  <!-- Question Details (Expanded Content) -->
                  @if (isQuestionExpanded(question.itemId)) {
                    <div class="px-4 pb-4 bg-gray-50/30">
                      <div class="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{{ question.feedback }}</div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- No Data State -->
      <div class="flex flex-col items-center justify-center py-16 text-gray-400">
        <lucide-icon [img]="icons.FileText" class="w-8 h-8 mb-3"></lucide-icon>
        <span class="text-sm">No details available</span>
      </div>
    }
  </div>
</div>
